import { useState, useEffect, useRef } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { format } from "date-fns";
import { CalendarIcon, ChevronsUpDown, Check } from "lucide-react";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import { daily_work_status } from "@/models/daily-work.model";
import { getVendorProperties, createDailyWork } from "@/app/vendors/daily-work/daily-work.api";
import { MonthlyPriceField, parseMonthlyPrice } from "@/app/vendors/daily-work/components/monthly-price-field";
import { store } from "@/lib/store";

interface CreateDailyWorkModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess: () => void;
}

export default function CreateDailyWorkModal({ open, onOpenChange, onSuccess }: CreateDailyWorkModalProps) {
  const [loading, setLoading] = useState(false);
  const [properties, setProperties] = useState<{ id: string; name: string; address?: string }[]>([]);
  const [property, setProperty] = useState("");
  const [status, setStatus] = useState<daily_work_status>(daily_work_status.active);
  const [startDate, setStartDate] = useState<Date | undefined>(undefined);
  const [endDate, setEndDate] = useState<Date | undefined>(undefined);
  const [monthlyPrice, setMonthlyPrice] = useState("");
  const [startDateOpen, setStartDateOpen] = useState(false);
  const [endDateOpen, setEndDateOpen] = useState(false);
  const [propertyComboboxOpen, setPropertyComboboxOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (open) {
      getVendorProperties()
        .then(setProperties)
        .catch((error) => {
          console.error("Failed to fetch properties:", error);
          toast.error("Failed to load properties");
        });
    }
  }, [open]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!property) {
      toast.error("Please select a property");
      return;
    }

    if (!startDate) {
      toast.error("Please select a start date");
      return;
    }

    const parsedMonthlyPrice = parseMonthlyPrice(monthlyPrice);
    if (parsedMonthlyPrice === undefined) {
      toast.error("Monthly price must be a non-negative amount");
      return;
    }

    setLoading(true);
    try {
      const vendorOrgId = store.getState().organization.activeOrgId;
      await createDailyWork({
        property,
        vendor: vendorOrgId,
        status,
        start_date: format(startDate, "yyyy-MM-dd"),
        end_date: endDate ? format(endDate, "yyyy-MM-dd") : null,
        monthly_price: parsedMonthlyPrice,
      });
      toast.success("Daily work created successfully");
      onSuccess();
      // Reset form
      setProperty("");
      setStatus(daily_work_status.active);
      setStartDate(undefined);
      setEndDate(undefined);
      setMonthlyPrice("");
    } catch (error: any) {
      toast.error(error.message || "Failed to create daily work");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Create Property Contract</DialogTitle>
          <DialogDescription>Set up the Daily Work contract for a property, then build its recurring schedule</DialogDescription>
        </DialogHeader>
        <div ref={containerRef}>
        <form onSubmit={handleSubmit}>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="property">Property *</Label>
              <Popover open={propertyComboboxOpen} onOpenChange={setPropertyComboboxOpen}>
                <PopoverTrigger asChild>
                  <Button
                    type="button"
                    variant="outline"
                    role="combobox"
                    aria-expanded={propertyComboboxOpen}
                    className="w-full justify-between font-normal"
                  >
                    {property
                      ? properties.find((prop) => prop.id === property)?.name || "Select a property"
                      : "Select a property"}
                    <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-[var(--radix-popover-trigger-width)] min-w-[var(--radix-popover-trigger-width)] p-0" align="start" container={containerRef.current}>
                  <Command>
                    <CommandInput placeholder="Search property..." />
                    <CommandList>
                      <CommandEmpty>No properties found.</CommandEmpty>
                      <CommandGroup>
                        {properties.map((prop) => (
                          <CommandItem
                            key={prop.id}
                            value={prop.name}
                            onSelect={() => {
                              setProperty(prop.id === property ? "" : prop.id);
                              setPropertyComboboxOpen(false);
                            }}
                          >
                            <Check
                              className={cn(
                                "mr-2 h-4 w-4",
                                property === prop.id ? "opacity-100" : "opacity-0"
                              )}
                            />
                            <div className="flex flex-col">
                              <span>{prop.name}</span>
                              {prop.address && (
                                <span className="text-xs text-muted-foreground">{prop.address}</span>
                              )}
                            </div>
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
            </div>

            <div className="space-y-2">
              <Label htmlFor="status">Status</Label>
              <Select value={status} onValueChange={(value) => setStatus(value as daily_work_status)}>
                <SelectTrigger className="w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={daily_work_status.active}>Active</SelectItem>
                  <SelectItem value={daily_work_status.paused}>Paused</SelectItem>
                  <SelectItem value={daily_work_status.cancelled}>Cancelled</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Start Date *</Label>
                <Popover open={startDateOpen} onOpenChange={setStartDateOpen}>
                  <PopoverTrigger asChild>
                    <Button variant="outline" className="w-full justify-start text-left font-normal">
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {startDate ? format(startDate, "PPP") : <span>Pick a date</span>}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start" container={containerRef.current}>
                    <Calendar
                      mode="single"
                      selected={startDate}
                      onSelect={(date) => {
                        setStartDate(date);
                        setStartDateOpen(false);
                      }}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
              </div>

              <div className="space-y-2">
                <Label>End Date</Label>
                <Popover open={endDateOpen} onOpenChange={setEndDateOpen}>
                  <PopoverTrigger asChild>
                    <Button variant="outline" className="w-full justify-start text-left font-normal">
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {endDate ? format(endDate, "PPP") : <span>Pick a date</span>}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start" container={containerRef.current}>
                    <Calendar
                      mode="single"
                      selected={endDate}
                      onSelect={(date) => {
                        setEndDate(date);
                        setEndDateOpen(false);
                      }}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
              </div>
            </div>
            <MonthlyPriceField value={monthlyPrice} onChange={setMonthlyPrice} />
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={loading}>
              Cancel
            </Button>
            <Button type="submit" disabled={loading || !property || !startDate}>
              {loading ? "Creating..." : "Create"}
            </Button>
          </DialogFooter>
        </form>
        </div>
      </DialogContent>
    </Dialog>
  );
}
